import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Apple, Chrome, Download, Smartphone } from "lucide-react";

function setOrCreate(selector, attributes, value) {
  let element = document.head.querySelector(selector);
  const created = !element;
  if (!element) {
    element = document.createElement(attributes.tag);
    Object.entries(attributes.values || {}).forEach(([key, attrValue]) => element.setAttribute(key, attrValue));
    document.head.appendChild(element);
  }
  const previous = element.getAttribute(attributes.property);
  element.setAttribute(attributes.property, value);
  return () => {
    if (created) element.remove();
    else if (previous === null) element.removeAttribute(attributes.property);
    else element.setAttribute(attributes.property, previous);
  };
}

export default function StorePwaInstall({ companyId, company, autoOpen = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const storeName = company?.name || "متجري";
  const iconUrl = company?.logo_url || "/icon-512.png";

  useEffect(() => {
    const encodedId = encodeURIComponent(companyId || "");
    const restore = [];
    const manifest = document.head.querySelector('link[rel="manifest"]');
    if (manifest) {
      const previous = manifest.getAttribute("href");
      let manifestUrl = `/api/store/public/${encodedId}/app-manifest.json`;
      let objectUrl = null;
      // The preview has no backend route handler; create the same per-store manifest in memory.
      if (process.env.REACT_APP_MOCK_MODE === "true") {
        const startPath = `/store/${encodedId}`;
        const icon = company?.logo_url || "/icon-512.png";
        const previewManifest = { id: startPath, name: storeName, short_name: storeName.slice(0, 16), description: `تسوق من ${storeName}`, lang: "ar", dir: "rtl", start_url: `${window.location.origin}${startPath}?from=installed-app`, scope: `${window.location.origin}${startPath}`, display: "standalone", orientation: "portrait-primary", theme_color: "#0f766e", background_color: "#f8fafc", icons: [{ src: icon, sizes: "192x192", type: icon.startsWith("data:") ? "image/png" : "image/png", purpose: "any" }, { src: icon, sizes: "512x512", type: "image/png", purpose: "any maskable" }] };
        objectUrl = URL.createObjectURL(new Blob([JSON.stringify(previewManifest)], { type: "application/manifest+json" }));
        manifestUrl = objectUrl;
      }
      manifest.setAttribute("href", manifestUrl);
      restore.push(() => { if (objectUrl) URL.revokeObjectURL(objectUrl); previous === null ? manifest.removeAttribute("href") : manifest.setAttribute("href", previous); });
    }
    const favicon = document.head.querySelector('link[rel="icon"]');
    if (favicon) {
      const previous = favicon.getAttribute("href");
      favicon.setAttribute("href", iconUrl);
      restore.push(() => previous === null ? favicon.removeAttribute("href") : favicon.setAttribute("href", previous));
    }
    restore.push(setOrCreate('link[rel="apple-touch-icon"]', { tag: "link", values: { rel: "apple-touch-icon", sizes: "180x180" }, property: "href" }, iconUrl));
    restore.push(setOrCreate('meta[name="apple-mobile-web-app-title"]', { tag: "meta", values: { name: "apple-mobile-web-app-title" }, property: "content" }, storeName.slice(0, 16)));
    const previousTitle = document.title;
    document.title = `${storeName} | متجر إلكتروني`;

    const onPrompt = event => { event.preventDefault(); setDeferredPrompt(event); };
    const onInstalled = () => { setDeferredPrompt(null); setOpen(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      restore.reverse().forEach(fn => fn());
      document.title = previousTitle;
    };
  }, [companyId, storeName, iconUrl]);

  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen, companyId]);

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setOpen(false);
    }
  };

  return (
    <>
      <Button type="button" onClick={() => setOpen(true)} className="fixed bottom-5 end-5 z-30 h-12 gap-2 rounded-full px-4 shadow-xl" data-testid="install-store-pwa-button">
        {company?.logo_url ? <img src={company.logo_url} alt="" className="h-6 w-6 rounded-full bg-white object-contain p-0.5" /> : <Download className="w-4 h-4" />}
        <span className="max-w-40 truncate">تثبيت {storeName}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-primary" /> تثبيت تطبيق {storeName}</DialogTitle>
            <DialogDescription>سيُضاف هذا المتجر كتطبيق مستقل باسمه وشعاره، ويفتح مباشرة على صفحة متجره.</DialogDescription>
          </DialogHeader>
          {company?.logo_url && <div className="flex items-center justify-center gap-3 rounded-xl bg-muted/40 p-4"><img src={company.logo_url} alt={`شعار ${storeName}`} className="h-14 w-14 rounded-xl border bg-white object-contain p-1" /><strong className="text-lg">{storeName}</strong></div>}
          {deferredPrompt ? <div className="space-y-3 text-sm"><p>اضغط الزر لإضافة تطبيق هذا المتجر إلى شاشة هاتفك.</p><Button onClick={install} className="w-full gap-2"><Download className="w-4 h-4" /> تثبيت التطبيق الآن</Button></div> : <div className="space-y-3 text-sm">
            <div className="rounded-xl border p-3"><div className="flex items-center gap-2 font-bold mb-2"><Chrome className="w-4 h-4" /> Android</div><p className="text-muted-foreground">من قائمة المتصفح (⋮) اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية». سيستخدم المتصفح اسم وشعار هذا المتجر.</p></div>
            <div className="rounded-xl border p-3"><div className="flex items-center gap-2 font-bold mb-2"><Apple className="w-4 h-4" /> iPhone</div><p className="text-muted-foreground">افتح الرابط في Safari، اضغط زر المشاركة، ثم اختر «إضافة إلى الشاشة الرئيسية».</p></div>
            <p className="text-xs text-muted-foreground">إذا لم يظهر خيار التثبيت فورًا، انتظر بضع ثوانٍ بعد تحميل الصفحة ثم افتح قائمة المتصفح.</p>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>إغلاق</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
